import { Link } from 'react-router';
import { Activity, Clock, ArrowRight, CalendarDays, Vibrate } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { ActivityTypeIcon } from '@/components/features/activities/ActivityTypeIcon';
import { useAuth } from '@/hooks/useAuth';
import { useActivities } from '@/hooks/useActivities';
import {
  formatDuration,
  getActivityAnalysisState,
  getActivityDisplayTitle,
  getVibrationModeLabel,
} from '@/types/activity';
import { getUserDisplayName } from '@/types/auth';
import { formatDateTime } from '@/utils/dates';

export function PatientDashboard() {
  const { user } = useAuth();
  // Use isCurrentPatient flag to load data from /patient/profile/
  const { activities, isLoading } = useActivities({ isCurrentPatient: true });
  const displayName = getUserDisplayName(user);

  const totalDuration = activities.reduce((sum, a) => sum + (a.duration || 0), 0);
  const recentActivities = activities.slice(0, 5);
  const latestActivity = recentActivities[0];
  const remainingRecentActivities = recentActivities.slice(1);
  const latestAnalysisState = latestActivity
    ? getActivityAnalysisState(latestActivity)
    : undefined;

  const stats = [
    {
      label: 'Total Activities',
      value: activities.length.toString(),
      icon: Activity,
      color: 'bg-primary-100 text-primary',
    },
    {
      label: 'Total Duration',
      value: formatDuration(totalDuration),
      icon: Clock,
      color: 'bg-secondary-100 text-secondary',
    },
  ];

  return (
    <div>
      <PageHeader
        title={`Welcome, ${displayName}`}
        description="Track your activity and view your health data"
      />

      {/* Stats */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} className="h-28" />
            ))
          : stats.map((stat) => (
              <Card key={stat.label}>
                <CardBody className="flex items-center gap-4">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-lg ${stat.color}`}
                  >
                    <stat.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {stat.value}
                    </p>
                    <p className="text-sm text-muted">{stat.label}</p>
                  </div>
                </CardBody>
              </Card>
            ))}
      </div>

      {/* Recent Activities */}
      <Card>
        <CardHeader
          title="Recent Activities"
          description="Your most recent sessions and what happened in each one"
          action={
            <Link to="/patient/activities">
              <Button variant="ghost" size="sm">
                View all
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          }
        />
        <CardBody>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : recentActivities.length === 0 ? (
            <p className="py-8 text-center text-muted">
              No activities recorded yet
            </p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
              {latestActivity && (
                <div className="rounded-2xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-primary-100/70 p-5 transition-shadow hover:shadow-card-hover">
                  <div className="mb-4 flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <ActivityTypeIcon type={latestActivity.type} size="lg" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">
                          Latest Session
                        </p>
                        <h3 className="text-xl font-semibold text-foreground">
                          {getActivityDisplayTitle(latestActivity)}
                        </h3>
                      </div>
                    </div>
                    {latestAnalysisState && (
                      <Badge variant={latestAnalysisState === 'failed' ? 'danger' : 'warning'}>
                        {latestAnalysisState === 'failed' ? 'Failed' : 'Pending Analysis'}
                      </Badge>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl bg-white/80 p-3">
                      <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted">
                        <CalendarDays className="h-4 w-4" />
                        Recorded
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {formatDateTime(latestActivity.timestamp)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-white/80 p-3">
                      <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted">
                        <Vibrate className="h-4 w-4" />
                        Vibration
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {getVibrationModeLabel(latestActivity.vibrationMode)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted">
                    <Badge variant="outline">
                      {getActivityDisplayTitle({ title: latestActivity.type, type: latestActivity.type })}
                    </Badge>
                    {latestActivity.duration !== undefined && (
                      <span>{formatDuration(latestActivity.duration)}</span>
                    )}
                    <span className="font-mono text-xs">
                      {latestActivity.activityStreamId || latestActivity.id}
                    </span>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">
                    Previous Sessions
                  </p>
                  <p className="text-xs text-muted">
                    {recentActivities.length} shown
                  </p>
                </div>

                {remainingRecentActivities.length > 0 ? (
                  remainingRecentActivities.map((activity) => {
                    const analysisState = getActivityAnalysisState(activity);

                    return (
                      <div key={activity.id} className="flex items-center gap-3 rounded-xl border border-primary-100/70 bg-primary-50/40 px-4 py-3 transition-colors hover:bg-white">
                        <ActivityTypeIcon type={activity.type} size="sm" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="truncate font-medium text-foreground">
                              {getActivityDisplayTitle(activity)}
                            </p>
                            {analysisState && (
                              <Badge
                                variant={analysisState === 'failed' ? 'danger' : 'warning'}
                              >
                                {analysisState === 'failed' ? 'Failed' : 'Pending'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted">
                            {formatDateTime(activity.timestamp)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-foreground">
                            {getVibrationModeLabel(activity.vibrationMode)}
                          </p>
                          <p className="text-xs text-muted">
                            {getActivityDisplayTitle({ title: activity.type, type: activity.type })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-xl border border-dashed border-border px-4 py-6 text-sm text-muted">
                    No other recent sessions yet.
                  </div>
                )}
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
